'use client';

import { IVipPrice } from '@/api/types/public';
import Image from '@/components/Image';
import PriceComponent from '@/components/Product/Price';
import SoldOutBlock from '@/components/Product/SoldOutBlock';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import Divider from '@mui/material/Divider';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import usePageTrack from '../hooks/usePageTrack';
import { IProduct } from './CollectionItemPcB';

interface IProps {
  data: [IProduct, IProduct];
  isReverse: boolean;
  index: number;
  reportProductExp: (product: IProduct, index: number, type?: 'exp' | 'click') => void;
}

export default function CollectionItemMB({
  data,
  isReverse = false,
  index,
  reportProductExp,
}: IProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const waiMode = useStore((state) => state.waiMode);
  const inAppH5 = useStore((state) => state.inAppH5);
  const [activeIndex, setActiveIndex] = useState(0);
  const { productRef, productInView, productBackRef, productBackInView } = usePageTrack({});
  const { navigate } = useAppNavigation();
  const handleSlideChange = (swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex);
  };
  const clickSlider = (i: number) => {
    setActiveIndex(i);
    swiperRef.current.slideTo(i);
  };
  useEffect(() => {
    if (productInView) {
      reportProductExp(data[0], index, 'exp');
    }
  }, [productInView]);
  useEffect(() => {
    if (productBackInView) {
      reportProductExp(data[1], index, 'exp');
    }
  }, [productBackInView]);
  const clickProduct = (e, item: IProduct, type: 'click' | 'exp') => {
    reportProductExp(item, index, type);
    if (inAppH5) {
      e.preventDefault();
      navigate(`/goods-detail/${item.shop_sku}`);
    }
  };
  return (
    <div
      className={`${!isReverse ? 'flex-row' : 'flex-row-reverse'}  flex bg-white rounded-[10px]`}
    >
      <div className="flex-1 flex items-center flex-col p-[20px_12px] max-w-[50%]">
        {data.map((item, i) => {
          const isSoldOutOrNoRestock = item.in_stock !== '1' || item.status === 2;
          return (
            <React.Fragment key={i}>
              <div className="w-full relative">
                <Link
                  href={`/goods-detail/${item.shop_sku}`}
                  ref={i === 0 ? productRef : productBackRef}
                  onClick={(e) => clickProduct(e, item, 'click')}
                >
                  <div className={`${isSoldOutOrNoRestock ? 'opacity-50' : ''}`}>
                    <Image
                      src={item.image}
                      alt={item.name}
                      width={115}
                      height={63}
                      className="mb-[12px] mx-auto"
                    />
                    <div className="text-[12px]/[14px] mb-[12px] text-[#808080] text-center">
                      {item.short_name}
                    </div>
                  </div>
                  <SoldOutBlock product={item} />
                  <PriceComponent
                    priceObject={item as IVipPrice}
                    renderPrice={(price, sellPrice) => (
                      <div className="mt-6 flex items-center justify-center">
                        <span className="text-14 font-bb leading-[17px] notranslate">
                          {sellPrice}
                        </span>
                        {price && (
                          <span
                            className={`ml-6 text-12 leading-[17px] line-through ${waiMode ? 'text-primary-black' : 'text-secondary-text'}`}
                          >
                            {price}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </Link>
              </div>
              {i === 0 && (
                <div className="w-full px-[12px] my-[15px]">
                  <Divider />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="flex-1 relative overflow-x-hidden">
        <Swiper
          direction="horizontal"
          slidesPerView={1}
          onSlideChange={handleSlideChange}
          onInit={(swiper) => {
            swiperRef.current = swiper;
          }}
          className="h-full"
        >
          {data.map((item) => (
            <SwiperSlide key={item.config_image} className="h-full  rounded-[10px] relative">
              <Link
                href={`/goods-detail/${item.shop_sku}`}
                onClick={(e) => clickProduct(e, item, 'click')}
              >
                <Image
                  src={item.config_image}
                  alt={item.name}
                  width={180}
                  height={279}
                  className="object-cover h-full w-full rounded-[10px]"
                />
                <div
                  className="absolute bottom-[0px] pb-[8px] w-full px-[20px] rounded-b-[10px]"
                  style={{
                    background: 'linear-gradient( 180deg, rgba(0,0,0,0) 0%, #000000 100%)',
                  }}
                >
                  <div className="text-[20px]/[24px] mb-[4px] text-white text-center font-be">
                    {item.short_name}
                  </div>
                  <div className="text-[12px]/[14px] mb-[9px] text-white text-center font-br">
                    {item.name}
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
        {/* 下方滑动框，写到外面是不想和里面的 slide混在一起 */}
        <div className="w-[60px] h-[3px] z-10 mx-auto absolute bottom-[8px] rounded-[3px] left-[50%] translate-x-[-50%]">
          <div
            className="w-[60px] h-full bg-white opacity-40 absolute top-0 left-0 rounded-[3px]"
            onClick={() => clickSlider(Number(!activeIndex))}
          />
          <div
            className={`collection-item-prev transition-all w-[30px] h-full bg-white rounded-[3px] ${activeIndex === 0 ? 'translate-x-0' : 'translate-x-[100%]'}`}
          />
        </div>
      </div>
    </div>
  );
}
